/**
 * Converts a state name to its two-letter code if it's in the allowed list
 * Also handles specific country codes for Hong Kong, Netherlands, and Finland
 * @param stateName - The name of the state or country to convert
 * @returns The two-letter state/country code or null if not allowed or not found
 */
export function getStateCode(stateName: string) {
  // List of allowed state and country codes
  const ALLOWED_CODES = ["PA", "RI", "NC", "NY", "CT", "NH", "CA", "IL", "VA", "GA", "HK", "NL", "TX"];
  
  // Map of all 50 state names to their codes
  const STATE_MAP: Record<string, string> = {
    "alabama": "AL",
    "alaska": "AK",
    "arizona": "AZ",
    "arkansas": "AR",
    "california": "CA",
    "colorado": "CO",
    "connecticut": "CT",
    "delaware": "DE",
    "florida": "FL",
    "georgia": "GA",
    "hawaii": "HI",
    "idaho": "ID",
    "illinois": "IL",
    "indiana": "IN",
    "iowa": "IA",
    "kansas": "KS",
    "kentucky": "KY",
    "louisiana": "LA",
    "maine": "ME",
    "maryland": "MD",
    "massachusetts": "MA",
    "michigan": "MI",
    "minnesota": "MN",
    "mississippi": "MS",
    "missouri": "MO",
    "montana": "MT",
    "nebraska": "NE",
    "nevada": "NV",
    "new hampshire": "NH",
    "new jersey": "NJ",
    "new mexico": "NM",
    "new york": "NY",
    "north carolina": "NC",
    "north dakota": "ND",
    "ohio": "OH",
    "oklahoma": "OK",
    "oregon": "OR",
    "pennsylvania": "PA",
    "rhode island": "RI",
    "south carolina": "SC",
    "south dakota": "SD",
    "tennessee": "TN",
    "texas": "TX",
    "utah": "UT",
    "vermont": "VT",
    "virginia": "VA",
    "washington": "WA",
    "west virginia": "WV",
    "wisconsin": "WI",
    "wyoming": "WY"
  };
  
  // Map of supported countries to their codes
  const COUNTRY_MAP: Record<string, string> = {
    "hong kong": "HK",
    "netherlands": "NL",
    "finland": "FI"
  };

  // Handle input validation
  if (!stateName || typeof stateName !== 'string') {
    return null;
  }

  // Normalize input: trim whitespace and convert to lowercase
  const normalizedInput = stateName.trim().toLowerCase();
  
  // For inputs that might be abbreviations already
  if (normalizedInput.length === 2 && normalizedInput === normalizedInput.toUpperCase()) {
    return ALLOWED_CODES.includes(normalizedInput) ? normalizedInput : null;
  }
  
  // Look up the state code
  const stateCode = STATE_MAP[normalizedInput] as string | undefined;
  
  // Check if the state code exists and is in the allowed list
  if (stateCode && ALLOWED_CODES.includes(stateCode)) {
    return stateCode;
  }
  
  // Check if it's one of our supported countries
  const countryCode = COUNTRY_MAP[normalizedInput];
  if (countryCode && ALLOWED_CODES.includes(countryCode)) {
    return countryCode;
  }
  
  return null;
}